// `mapTempCriteriaToExtensionCriteria` and the `map*Criteria` functions it calls
// (search.page.component.ng1.ts:724) — the form's state, turned back into the wire shape.
//
// The inverse of `criteriaLoad.ts`, and it is NOT a mirror image: loading fills in defaults,
// mapping back STRIPS them. Every one of these returns `undefined` for a section the user has
// not actually filled in, and `pruneEmptyCriteria` then removes the key — which is what makes
// "Filters used n/9", the empty-form guard and the prediction's skip-when-empty all work.
//
// ⚠️ THE DEFAULTS THE LOADER PUTS IN WOULD OTHERWISE COUNT AS CONTENT. `loadWorkExperience`
// gives all six blocks `{ period: ANY_TIME }` and turnover `{ minTurnoverRate: 1, condition:
// EXCLUDE }`, because that is what the selects must show at rest. Spreading that state straight
// into the criteria object marks Work experience used on a blank form and fires a prediction
// request production does not send. The form shape is not the wire shape in EITHER direction.
import type { FieldOfExpertiseRow } from './FieldOfExpertiseSection.tsx'
import type { SkillCriteriaRow } from './SkillSetSection.tsx'
import type { WorkExperienceCriteria } from './WorkExperienceSection.tsx'
import type { KeywordCondition } from './KeywordSearchSection.tsx'

/**
 * `getYearsRange` — ⚠️ NULL WHEN BOTH ARE NULL, not an object of nulls. A `{min: null, max:
 * null}` on the wire is a range the server tries to apply.
 *
 * ⚠️ AND IT IS `null`, NOT `undefined`. The two are the same to TypeScript and not to
 * `JSON.stringify`: `undefined` drops the key, `null` keeps it. Production build 354 sends
 * `"yoe": null` and `"yearsRange": null` on a row with no years, where this port sent no key
 * at all — the last difference left on those two arrays after the empty-row defaults landed.
 */
export function yearsRange(
  min: number | null | undefined,
  max: number | null | undefined,
): { min: number | null; max: number | null } | null {
  if ((min ?? null) === null && (max ?? null) === null) return null
  return { min: min ?? null, max: max ?? null }
}

/**
 * `validateBooleanCriteria` — a block counts as filled in only when one of its keys holds a
 * boolean `true`.
 *
 * ⚠️ IT IGNORES EVERY NON-BOOLEAN. A period, a rate or a condition alone never makes a block
 * valid, which is precisely why the loader's defaults are harmless on the way out.
 */
export function hasTickedBoolean(block: object | undefined): boolean {
  if (block === undefined) return false
  return Object.values(block).some((value: unknown) => typeof value === 'boolean' && value)
}

export function mapFieldOfExpertise(
  rows: readonly FieldOfExpertiseRow[],
  operator: number | null,
  otherFieldsFieldName?: string,
): unknown[] | undefined {
  // Only rows with a FIELD count — a position alone is not a criterion.
  //
  // ⚠️ AND A SENTINEL-ONLY ROW DOES NOT COUNT EITHER. Upstream filters TWICE
  // (`mapFieldOfExpertiseCriteria`, search.page.component.ng1.ts:1388): after `!!foe.field` it
  // applies `isOtherPositionsFieldOfExpertiseCriteriaValid`, which rejects a row whose field is
  // the "Other fields" sentinel and which carries no `otherPositions`.
  //
  // Dropping that second filter is not a small omission, because the row the screen LOADS with
  // is exactly that shape: `emptyFieldOfExpertiseRow` seeds `field = otherFieldsFieldName`
  // whenever `EXTENDED_TITLE_CLASS_SEARCH_CLIENT_SIDE` is on, so on a tenant with the flag the
  // blank form emitted a `fieldOfExpertise` key and the left rail read "Filters used 1/9" with
  // the section bold, where production reads 0/9.
  //
  // ⚠️ IT PRESENTED AS FLAKE, WHICH IS WHY IT SURVIVED A FIX. `otherFieldsFieldName` and the
  // flag both arrive with the view model while the criteria arrive with the alert, so whichever
  // resolved first decided the count: the sweep scored this route 0.25% or 0.01% at random, per
  // width, and two clean runs in a row read as "fixed". With the filter the row is excluded
  // whether or not the sentinel has landed, so the count is 0/9 either way.
  const valid = rows
    .filter((row) => row.field !== undefined && row.field !== '')
    .filter((row) => (row.field === otherFieldsFieldName ? (row.otherPositions?.length ?? 0) > 0 : true))
  if (valid.length === 0) return undefined
  // ⚠️ MORE THAN ONE ROW WITHOUT AN OPERATOR EMITS NOTHING AT ALL. Upstream returns early, so
  // a half-finished second row silently withdraws the whole section rather than sending the
  // first row on its own.
  if (rows.length > 1 && operator === null) return undefined

  return valid.map((row) => ({
    field: row.field,
    // An empty string becomes undefined; the server treats '' as a position to match.
    position: row.position === '' ? undefined : row.position,
    // ⚠️ ONLY ON THE "OTHER" ROWS, and only when non-empty. Upstream guards on the sentinel
    // as well as the length, so a row that once was Other and is not any more stops sending
    // titles rather than carrying them under a field that has no use for them.
    otherPositions:
      otherFieldsFieldName !== undefined &&
      row.field === otherFieldsFieldName &&
      (row.otherPositions?.length ?? 0) > 0
        ? (row.otherPositions ?? []).map((tag) => tag.text)
        : undefined,
    yoe: yearsRange(row.minYears, row.maxYears),
    when: row.when,
  }))
}

export function mapSkills(rows: readonly SkillCriteriaRow[]): unknown[] | undefined {
  const valid = rows.filter((row) => (row.skills?.length ?? 0) > 0)
  if (valid.length === 0) return undefined
  return valid.map((row) => ({
    // Back to plain strings — the tag objects are a form-only shape.
    skills: (row.skills ?? []).map((skill) => skill.text),
    yearsRange: yearsRange(row.minYears, row.maxYears),
    lastUsed: row.lastUsed,
    allowUnknownYears: row.allowUnknownYears,
  }))
}

export function mapKeywords(conditions: readonly KeywordCondition[]): { conditions: unknown[] } | undefined {
  const valid = conditions.filter((condition) => (condition.terms?.length ?? 0) > 0)
  if (valid.length === 0) return undefined
  return {
    conditions: valid.map((condition) => ({
      sectionCategory: condition.sectionCategory,
      conditionType: condition.conditionType,
      terms: (condition.terms ?? []).map((term) => term.text),
    })),
  }
}

/**
 * The six work-experience blocks, each independently withdrawn when empty.
 *
 * ⚠️ `important` NEVER GOES BACK ON THE WIRE. It is the form's own representation of "this
 * criterion exists" — `mapCompanyTiersCriteria` returns `{ period }` alone. Sending it back
 * adds a field the server does not know, and reading the round trip as lossless hides that.
 */
export function mapWorkExperience(criteria: WorkExperienceCriteria): Record<string, unknown> {
  const mapped: Record<string, unknown> = {}

  const industries = criteria.industries?.industries ?? []
  if (industries.length > 0) {
    mapped['industries'] = {
      industries: industries.map((tag) => ({ text: tag.text })),
      period: criteria.industries?.period,
    }
  }

  const companies = criteria.companies?.companies ?? []
  if (companies.length > 0) {
    // ⚠️ STRINGS back out, objects in — the opposite direction of the same asymmetry the
    // loader handles.
    mapped['companies'] = { companies: companies.map((tag) => tag.text), period: criteria.companies?.period }
  }

  if (hasTickedBoolean(criteria.companyTypes)) {
    const types: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(criteria.companyTypes ?? {})) {
      if (value === true) types[key] = true
    }
    // The period rides along whether or not it was ticked, when the block is valid at all.
    if (criteria.companyTypes?.period !== undefined) types['period'] = criteria.companyTypes.period
    mapped['companyTypes'] = types
  }

  if (hasTickedBoolean(criteria.companyTiers)) mapped['companyTiers'] = { period: criteria.companyTiers?.period }
  if (hasTickedBoolean(criteria.qualityCompanyOnly)) {
    mapped['qualityCompanyOnly'] = { period: criteria.qualityCompanyOnly?.period }
  }
  if (hasTickedBoolean(criteria.candidateTurnover)) {
    mapped['candidateTurnover'] = {
      minTurnoverRate: criteria.candidateTurnover?.minTurnoverRate,
      condition: criteria.candidateTurnover?.condition,
    }
  }

  return mapped
}

/**
 * `mapEducationCriteria` (`search.page.component.ng1.ts:1692`) — the inverse of
 * `loadEducationSection`, and it was missing entirely.
 *
 * ⚠️ THE FORM IS KEYED BY DISPLAY GROUP, THE WIRE BY VALUE, AND WE WERE SENDING THE GROUP.
 * `loadEducation` folds `{ba: true, bsc: true}` into the single checkbox `"BA / BsC"`; nothing
 * expanded it back, so Save sent `{"BA / BsC": true}`. The server took it (200) and the alert
 * reopened with NO boxes ticked, because `loadEducationSection` then looked for `ba`/`bsc` and
 * found a key it does not know — measured against production build 354, which sent
 * `{ba: true, bsc: true}` and restored both ticks.
 *
 * ⚠️ UPSTREAM'S `isAllSelected` GUARD IS A NO-OP IN THIS SHAPE, and replicating it literally
 * would be cargo cult. It asks whether every key of the raw enum (`ba`, `bsc`, `ma`, …) is
 * truthy in the selection — but the selection is keyed by display group (`"BA / BsC"`), so the
 * lookup is always `undefined` and the branch always expands. Production's behaviour on the
 * wire confirms it: one ticked group produced the expanded values, never an omitted key.
 */
export function mapEducationSection(
  section: Readonly<Record<string, boolean>> | undefined,
  groups: readonly { key: string; values?: string[] }[],
): Record<string, boolean> | undefined {
  if (section === undefined) return undefined
  const expanded: Record<string, boolean> = {}
  for (const key of Object.keys(section)) {
    if (section[key] !== true) continue
    const group = groups.find((candidate) => candidate.key === key)
    // A key with no matching group is passed through: it is already a wire value, which is
    // what a criteria object loaded from the server and never touched looks like.
    if (group === undefined) expanded[key] = true
    else for (const value of group.values ?? []) expanded[value] = true
  }
  return expanded
}

export interface EducationLikeCriteria {
  degreeType?: Record<string, boolean>
  fieldCategory?: Record<string, boolean>
  instituteBadge?: Record<string, boolean>
  degreeOrIDF?: boolean
}

export function mapEducation(
  criteria: EducationLikeCriteria,
  degreeGroups: readonly { key: string; values?: string[] }[],
  fieldGroups: readonly { key: string; values?: string[] }[],
): Record<string, unknown> {
  const mapped: Record<string, unknown> = {
    ...criteria,
    degreeType: mapEducationSection(criteria.degreeType, degreeGroups),
    fieldCategory: mapEducationSection(criteria.fieldCategory, fieldGroups),
    // Badges are not grouped — upstream copies the true keys across as they are.
    instituteBadge: mapEducationSection(criteria.instituteBadge, []),
  }
  // Upstream's trailing loop: `delete mappedEducation[key]` for every FALSE value.
  //
  // ⚠️ AND FOR EVERY UNDEFINED ONE, or the section never goes away. Upstream's `let degreeType;`
  // is simply never assigned when there is nothing to send, and `angular.toJson` drops an
  // undefined property — but the KEY still exists here, so `pruneEmptyCriteria` counted three
  // of them and kept the object. The wire then carried `"education": {}` on every prediction
  // from a form where nobody had ticked anything, where production carried no key at all.
  for (const key of Object.keys(mapped)) {
    const value = mapped[key]
    // ⚠️ AN EMPTY SECTION IS DROPPED TOO, or the whole criterion survives as three empty
    // objects. `pruneEmptyCriteria` only asks whether a value has NO keys, so
    // `{degreeType: {}, fieldCategory: {}, instituteBadge: {}}` counts as a filter in use:
    // the rail read "Filters used 2/9" with Education bold against production's 1/9, on an
    // alert whose education criteria are three empty objects. Measured side by side at 1146px.
    const isEmptyObject = typeof value === 'object' && value !== null && Object.keys(value).length === 0
    if (value === false || value === undefined || isEmptyObject) delete mapped[key]
  }
  return mapped
}
